import React, { useState, useEffect } from "react";
import "./DashboardPage.css";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../../services/api";
import { Session, CoachingPlan, User, Team, Scenario, Content } from "../../types";
import { Button } from "../shared/FormField";
import { SessionDetailPanel } from "../shared/SessionDetailPanel";
import { canAccess } from "../../config/access";

export function DashboardPage() {
  const { role, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [coachingPlan, setCoachingPlan] = useState<CoachingPlan | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [content, setContent] = useState<Content[]>([]);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, [role]);

  const loadDashboardData = async () => {
    try {
      const [sessionsResult, planResult] = await Promise.all([
        apiGet<{ items: Session[] }>("/api/sessions"),
        apiGet<CoachingPlan | { plan: null }>("/api/coaching/plan"),
      ]);
      setSessions(sessionsResult.items);
      if (planResult && !("plan" in planResult && planResult.plan === null)) {
        setCoachingPlan(planResult as CoachingPlan);
      }

      if (canAccess(role, ["admin"])) {
        const [usersResult, teamsResult, scenariosResult, contentResult] = await Promise.all([
          apiGet<{ items: User[] }>("/api/users"),
          apiGet<{ items: Team[] }>("/api/teams"),
          apiGet<{ items: Scenario[] }>("/api/scenarios"),
          apiGet<{ items: Content[] }>("/api/content"),
        ]);
        setUsers(usersResult.items);
        setTeams(teamsResult.items);
        setScenarios(scenariosResult.items);
        setContent(contentResult.items);
      }
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const completedSessions = sessions.filter((s) => s.status === "completed");
  const scoredSessions = completedSessions.filter((s) => s.score);
  const avgScore = scoredSessions.length > 0
    ? Math.round(scoredSessions.reduce((sum, s) => sum + (s.score?.overallScore || 0), 0) / scoredSessions.length)
    : null;

  const thisWeek = new Date();
  thisWeek.setDate(thisWeek.getDate() - 7);
  const sessionsThisWeek = sessions.filter((s) => new Date(s.createdAt) > thisWeek);

  const recentSessions = sessions.slice(0, 5);
  const topImprovement = coachingPlan?.improvementAreas?.[0];

  if (loading) return <div className="protrain-dashboard-loading">Loading dashboard...</div>;

  return (
    <div className="protrain-dashboard">
      <div className="protrain-dashboard-welcome">
        <h2>Welcome back, {user?.name?.split(" ")[0] || "there"}</h2>
        <p className="protrain-dashboard-subtitle">
          {role === "admin" ? "Platform overview" : role === "sales_manager" ? "Team performance at a glance" : "Your training progress"}
        </p>
      </div>

      {/* Quick Actions */}
      <div className="protrain-dashboard-actions">
        <Button onClick={() => navigate("/practice")}>🎭 Start Role-Play</Button>
        <Button variant="secondary" onClick={() => navigate("/practice")}>📄 Upload Transcript</Button>
        <Button variant="secondary" onClick={() => navigate("/learn")}>📱 Learn</Button>
      </div>

      {/* Stats Cards */}
      <div className="protrain-dashboard-stats">
        <div className="protrain-dashboard-stat-card">
          <div className="protrain-dashboard-stat-value">{completedSessions.length}</div>
          <div className="protrain-dashboard-stat-label">Sessions Completed</div>
        </div>
        <div className="protrain-dashboard-stat-card">
          <div className="protrain-dashboard-stat-value">{sessionsThisWeek.length}</div>
          <div className="protrain-dashboard-stat-label">This Week</div>
        </div>
        <div className="protrain-dashboard-stat-card">
          <div className="protrain-dashboard-stat-value protrain-dashboard-stat-score">
            {avgScore !== null ? avgScore : "—"}
          </div>
          <div className="protrain-dashboard-stat-label">Avg Score</div>
        </div>
        <div className="protrain-dashboard-stat-card">
          <div className="protrain-dashboard-stat-value">
            {coachingPlan ? coachingPlan.strengthScore : "—"}
          </div>
          <div className="protrain-dashboard-stat-label">Strength Score</div>
        </div>
      </div>

      <div className="protrain-dashboard-grid">
        {/* Recent Sessions */}
        <div className="protrain-dashboard-card">
          <div className="protrain-dashboard-card-header">
            <h3>Recent Sessions</h3>
            <button className="protrain-dashboard-link" onClick={() => navigate("/practice")}>View all →</button>
          </div>
          {recentSessions.length === 0 ? (
            <p className="protrain-dashboard-empty-text">No sessions yet. Start a role-play or upload a transcript.</p>
          ) : (
            <div className="protrain-dashboard-session-list">
              {recentSessions.map((s) => (
                <button
                  key={s.sessionId}
                  className="protrain-dashboard-session-row"
                  onClick={() => setSelectedSession(s)}
                >
                  <div className="protrain-dashboard-session-info">
                    <span className="protrain-dashboard-session-mode">
                      {s.mode === "text" ? "💬" : s.mode === "transcript" ? "📄" : "🎤"}
                    </span>
                    <span className="protrain-dashboard-session-date">
                      {new Date(s.createdAt).toLocaleDateString()}
                    </span>
                    <span className="protrain-dashboard-session-persona">{s.personaType}</span>
                  </div>
                  <div className="protrain-dashboard-session-score">
                    {s.score ? (
                      <span className={`protrain-dashboard-score-badge ${s.score.overallScore >= 80 ? "protrain-dashboard-score-badge--high" : s.score.overallScore >= 60 ? "protrain-dashboard-score-badge--mid" : "protrain-dashboard-score-badge--low"}`}>
                        {s.score.overallScore}
                      </span>
                    ) : (
                      <span className="protrain-dashboard-score-badge protrain-dashboard-score-badge--none">—</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Coaching Snapshot */}
        <div className="protrain-dashboard-card">
          <div className="protrain-dashboard-card-header">
            <h3>Coaching Snapshot</h3>
            <button className="protrain-dashboard-link" onClick={() => navigate("/coaching")}>Full plan →</button>
          </div>
          {coachingPlan ? (
            <div className="protrain-dashboard-coaching">
              <div className="protrain-dashboard-coaching-score">
                <span className="protrain-dashboard-coaching-score-value">{coachingPlan.strengthScore}</span>
                <span className="protrain-dashboard-coaching-score-label">Strength Score</span>
              </div>
              {topImprovement && (
                <div className="protrain-dashboard-coaching-focus">
                  <span className="protrain-dashboard-coaching-focus-label">Top Focus Area</span>
                  <span className="protrain-dashboard-coaching-focus-area">{topImprovement.area}</span>
                  <div className="protrain-dashboard-coaching-progress-bar">
                    <div className="protrain-dashboard-coaching-progress-fill" style={{ width: `${topImprovement.progress}%` }} />
                  </div>
                  <span className="protrain-dashboard-coaching-progress-text">{topImprovement.currentScore} → {topImprovement.targetScore}</span>
                </div>
              )}
              {coachingPlan.recommendedScenarios?.[0] && (
                <div className="protrain-dashboard-coaching-rec">
                  <span className="protrain-dashboard-coaching-rec-label">Recommended Practice</span>
                  <button
                    className="protrain-dashboard-link"
                    onClick={() => navigate(`/practice?scenario=${coachingPlan.recommendedScenarios[0].scenarioId}`)}
                  >
                    🎭 {coachingPlan.recommendedScenarios[0].scenarioName}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="protrain-dashboard-coaching-empty">
              <p>No coaching plan yet.</p>
              {scoredSessions.length > 0 ? (
                <Button onClick={() => navigate("/coaching")}>✨ Generate Plan</Button>
              ) : (
                <p className="protrain-dashboard-empty-text">Complete some sessions first to generate a coaching plan.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Admin Stats */}
      {canAccess(role, ["admin"]) && (
        <div className="protrain-dashboard-admin">
          <h3>Platform Overview</h3>
          <div className="protrain-dashboard-admin-stats">
            <div className="protrain-dashboard-admin-stat" onClick={() => navigate("/settings")}>
              <span className="protrain-dashboard-admin-stat-value">{users.length}</span>
              <span className="protrain-dashboard-admin-stat-label">Users</span>
            </div>
            <div className="protrain-dashboard-admin-stat" onClick={() => navigate("/settings")}>
              <span className="protrain-dashboard-admin-stat-value">{teams.length}</span>
              <span className="protrain-dashboard-admin-stat-label">Teams</span>
            </div>
            <div className="protrain-dashboard-admin-stat" onClick={() => navigate("/settings")}>
              <span className="protrain-dashboard-admin-stat-value">{scenarios.length}</span>
              <span className="protrain-dashboard-admin-stat-label">Scenarios</span>
            </div>
            <div className="protrain-dashboard-admin-stat" onClick={() => navigate("/settings")}>
              <span className="protrain-dashboard-admin-stat-value">{content.length}</span>
              <span className="protrain-dashboard-admin-stat-label">Content Items</span>
            </div>
          </div>
        </div>
      )}

      {/* Manager Team Quick View */}
      {canAccess(role, ["sales_manager", "admin"]) && (
        <div className="protrain-dashboard-card protrain-dashboard-team-card">
          <div className="protrain-dashboard-card-header">
            <h3>Team Activity</h3>
            <button className="protrain-dashboard-link" onClick={() => navigate("/team")}>Team dashboard →</button>
          </div>
          <p className="protrain-dashboard-empty-text">View your team's practice activity and scores in the Team Dashboard.</p>
        </div>
      )}

      <SessionDetailPanel
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
        onPracticeAgain={(scenarioId) => {
          setSelectedSession(null);
          navigate(`/practice?scenario=${scenarioId}`);
        }}
      />
    </div>
  );
}
